/*
 * Collection-card colour swatches.
 *
 * Mirrors the PDP swatch behaviour (sections/main-product-qf.liquid) for product
 * cards. Each card ships a curated server-side fallback (the metafield chain in
 * snippets/collection-card-qf.liquid). This script lazily upgrades each card,
 * when it scrolls into view, to show every colourway sharing the product's
 * style:xxxxx tag — fetched once per style via the api-related-swatches Section
 * Rendering endpoint and cached in sessionStorage under the SAME key the PDP
 * uses ("swatch:<style:xxxxx>"), so cards and PDPs share the cache.
 *
 * Lazy + cached is what keeps it cheap: only the cards actually on screen ever
 * trigger a request, and a style is fetched at most once per session.
 */
(function () {
  if (window.__qfCardSwatchesInit) return;
  window.__qfCardSwatchesInit = true;

  var DONE = 'qfSwatchesDone';     // dataset flag on processed containers
  var inflight = {};               // styleTag -> Promise<list>

  // Read the curated server-side swatches already in the container.
  function readFallback(container) {
    return Array.prototype.slice
      .call(container.querySelectorAll('.color-swatch'))
      .map(function (a) {
        return {
          hex: a.getAttribute('data-hex') || '',
          label: a.getAttribute('data-color') || '',
          url: a.getAttribute('href') || '#',
          handle: (a.getAttribute('data-handle') || '').toLowerCase(),
          active: a.classList.contains('color-swatch-active')
        };
      })
      .filter(function (s) { return s.hex && s.label; });
  }

  // Parse the api-related-swatches section HTML into swatch objects.
  function parseApi(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = String(html).trim();
    var results = tpl.content.querySelector('.related-swatches-results');
    if (!results) return [];
    return Array.prototype.slice
      .call(results.querySelectorAll('.color-swatch'))
      .map(function (a) {
        return {
          hex: a.getAttribute('data-hex') || '',
          label: a.getAttribute('data-color') || '',
          url: a.getAttribute('href') || '#',
          handle: (a.getAttribute('data-handle') || '').toLowerCase()
        };
      })
      .filter(function (s) { return s.hex && s.label; });
  }

  // Dedupe (by hex+label), sort by hex, render into the container.
  function renderSwatches(container, list) {
    var seen = {};
    var unique = [];
    list.forEach(function (s) {
      var key = s.hex + '-' + s.label;
      if (!seen[key]) { seen[key] = true; unique.push(s); }
    });
    unique.sort(function (a, b) { return a.hex.localeCompare(b.hex); });

    var html = '';
    unique.forEach(function (s) {
      var active = s.active ? ' color-swatch-active' : '';
      html +=
        '<a href="' + s.url + '" class="color-swatch' + active + '"' +
        ' style="border-color:' + s.hex + ';"' +
        ' data-hex="' + s.hex + '"' +
        ' data-handle="' + s.handle + '"' +
        ' data-color="' + s.label + '">' +
        '<span style="background-color:' + s.hex + ';"></span></a>';
    });
    container.innerHTML = html;
  }

  // Fetch (once) the colourways for a style tag, via cache or the API.
  function fetchStyle(styleTag) {
    if (inflight[styleTag]) return inflight[styleTag];

    var cacheKey = 'swatch:' + styleTag;
    var cached = null;
    try { cached = sessionStorage.getItem(cacheKey); } catch (e) {}
    if (cached) {
      inflight[styleTag] = Promise.resolve(parseApi(cached));
      return inflight[styleTag];
    }

    var url = '/search?q=' + encodeURIComponent('tag:"' + styleTag + '"') +
              '&type=product&section_id=api-related-swatches';

    inflight[styleTag] = fetch(url)
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        var list = parseApi(html);
        if (list.length) { try { sessionStorage.setItem(cacheKey, html); } catch (e) {} }
        return list;
      })
      .catch(function () { return []; });

    return inflight[styleTag];
  }

  function enhance(container) {
    if (container.dataset[DONE]) return;
    container.dataset[DONE] = '1';

    var styleTag = container.getAttribute('data-style-tag');
    if (!styleTag) return; // no style tag -> keep curated fallback

    var currentHandle = (container.getAttribute('data-current-handle') || '').toLowerCase();
    var fallback = readFallback(container);

    fetchStyle(styleTag).then(function (list) {
      if (!list.length) return; // network/empty -> keep curated fallback

      list = list.map(function (s) {
        return {
          hex: s.hex, label: s.label, url: s.url,
          handle: s.handle, active: s.handle === currentHandle
        };
      });

      // Make sure the current product is present and flagged active.
      if (!list.some(function (s) { return s.active; })) {
        var current = fallback.filter(function (s) { return s.active; })[0];
        if (current && current.hex && current.label) {
          current.active = true;
          list.push(current);
        }
      }

      renderSwatches(container, list);
    });
  }

  var io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            io.unobserve(entry.target);
            enhance(entry.target);
          }
        });
      }, { rootMargin: '200px 0px' })
    : null;

  function observe(container) {
    if (!container || container.dataset[DONE]) return;
    if (io) io.observe(container);
    else enhance(container); // no IntersectionObserver -> upgrade immediately
  }

  function scan(root) {
    var nodes = (root || document).querySelectorAll('.js-card-swatches');
    Array.prototype.forEach.call(nodes, observe);
  }

  function init() {
    scan(document);

    // Cards get removed/re-inserted by the collection filter, "load more", and
    // AJAX surfaces (recommendations, predictive search). Pick up new ones.
    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (node.classList && node.classList.contains('js-card-swatches')) observe(node);
            if (node.querySelectorAll) scan(node);
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
